import React, { useState, useEffect } from "react";
import { Link, useSearchParams, Routes, Route, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTenant } from "../context/TenantContext";
import FinancialDashboard from "../components/admin/FinancialDashboard";
import EnhancedStaffManagement from "../components/admin/EnhancedStaffManagement";
import AdminSettings from "../components/admin/AdminSettings";
import NewsManagement from "../components/admin/NewsManagement";
import HelpSupport from "../components/admin/HelpSupport";
import FirebaseMigration from "../components/admin/FirebaseMigration";
import SimpleFirebaseTest from "../components/admin/SimpleFirebaseTest";
import InitializeRoomsButton from "../components/admin/InitializeRoomsButton";
import TenantManagement from "../components/admin/TenantManagement";
import InvoiceDetail from "../components/admin/InvoiceDetail";
import FeedbackManagement from "./admin/FeedbackManagement";
import AdminBookingRequests from "../components/admin/AdminBookingRequests";
import CreateTestBookingData from "../components/admin/CreateTestBookingData";

const TABS = [
  { key: "financial", label: "Financial" },
  { key: "staff", label: "Staff" },
  { key: "booking-requests", label: "Booking Requests" },
  { key: "feedback", label: "Feedback" },
  { key: "news", label: "News" },
  { key: "help", label: "Help & Support" },
  { key: "settings", label: "Settings" },
];

function Admin() {
  const { isFirebaseAvailable } = useAuth();
  const { hasPermission, loading, userProfile } = useTenant();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [tab, setTab] = useState("financial");
  const [staffTabKey, setStaffTabKey] = useState(Date.now());

  // Check if we're on a sub-route (like invoice detail)
  const isSubRoute = location.pathname !== '/admin' && location.pathname.startsWith('/admin/');

  // Check URL parameters for initial tab and staff member
  useEffect(() => {
    const urlTab = searchParams.get('tab');
    const staffId = searchParams.get('staffId');
    
    if (urlTab && TABS.some(t => t.key === urlTab)) {
      setTab(urlTab);
    }
    
    // If navigating to staff tab with specific staff member, update key to trigger refresh
    if (urlTab === 'staff' && staffId) {
      setStaffTabKey(Date.now());
    }
  }, [searchParams]);

  // Show loading while tenant context is initializing
  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading admin panel...</p>
      </div>
    );
  }

  // Check admin permissions after loading is complete
  if (!hasPermission('admin')) {
    console.log('🚫 Admin access denied. UserProfile:', userProfile);
    return (
      <div className="p-8 text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
        <p className="text-gray-600">You don't have permission to access the admin panel.</p>
        <p className="text-sm text-gray-500 mt-2">Role: {userProfile?.role || 'Unknown'}</p>
      </div>
    );
  }

  function handleTabChange(key) {
    setTab(key);
    if (key === "staff") {
      setStaffTabKey(Date.now());
    }
  }

  // If we're on a sub-route, render the appropriate component
  if (isSubRoute) {
    return (
      <Routes>
        <Route path="/financial/invoices/:invoiceId" element={<InvoiceDetail />} />
      </Routes>
    );
  }

  return (
    // MODIFIED: Wrapped in a main container for consistent padding and spacing
    <div className="p-4 space-y-6">
      {/* Header */}
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900">Admin Panel</h1>
      </div>

      {/* Tabs (Styling is already consistent) */}
      <div className="flex space-x-4 border-b pb-2 mb-4">
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`px-3 py-1 rounded-t ${tab === t.key
                ? "bg-blue-100 border-b-2 border-blue-600 text-blue-700 font-semibold"
                : "text-gray-600 hover:bg-gray-100"
              }`}
            onClick={() => handleTabChange(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ADDED: White, shadowed container for the tab content */}
      <div className="bg-white p-6 rounded shadow min-h-[200px]">
        {tab === "financial" && <FinancialDashboard />}
        {tab === "staff" && <EnhancedStaffManagement key={staffTabKey} initialStaffId={searchParams.get('staffId')} />}
        {tab === "booking-requests" && (
          <div className="space-y-6">
            <CreateTestBookingData />
            <AdminBookingRequests />
          </div>
        )}
        {tab === "feedback" && <FeedbackManagement />}
        {tab === "news" && <NewsManagement />}
        {tab === "help" && <HelpSupport />}
        {tab === "settings" && <AdminSettings />}
      </div>
    </div>
  );
}

export default Admin;